import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'

const TOKEN_KEY = 'nymo_auth_token'
const WARDEN_BASE = import.meta.env.VITE_WARDEN_URL || 'https://warden.kcolb.com'
const WARDEN_CLIENT_ID = import.meta.env.VITE_WARDEN_CLIENT_ID || 'nymo'

type AuthUser = {
  id: string
  email: string
  name: string
  role: string
}

type AuthState = {
  user: AuthUser | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<void>
  register: (name: string, email: string, password: string) => Promise<void>
  logout: () => void
}

function getRawToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

function decodeToken(token: string): AuthUser | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return {
      id: payload.sub,
      email: payload.email || '',
      name: payload.name || '',
      role: payload.role || 'viewer',
    }
  } catch {
    return null
  }
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const token = getRawToken()
    if (token) {
      const decoded = decodeToken(token)
      if (decoded) {
        setUser(decoded)
      } else {
        localStorage.removeItem(TOKEN_KEY)
      }
    }
    setIsLoading(false)
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const res = await fetch(`${WARDEN_BASE}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, appId: WARDEN_CLIENT_ID }),
    })

    const json = await res.json()
    if (!res.ok || !json.success) {
      throw new Error(json.error || 'Login failed')
    }

    const { token, user: rawUser } = json.data
    localStorage.setItem(TOKEN_KEY, token)
    const decoded = decodeToken(token)
    setUser({
      id: rawUser.id,
      email: rawUser.email || email,
      name: rawUser.name,
      role: decoded?.role || 'viewer',
    })
  }, [])

  const register = useCallback(async (name: string, email: string, password: string) => {
    const res = await fetch(`${WARDEN_BASE}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, password, appId: WARDEN_CLIENT_ID }),
    })

    const json = await res.json()
    if (!res.ok || !json.success) {
      throw new Error(json.error || 'Registration failed')
    }

    const { token, user: rawUser } = json.data
    localStorage.setItem(TOKEN_KEY, token)
    const decoded = decodeToken(token)
    setUser({
      id: rawUser.id,
      email: rawUser.email || email,
      name: rawUser.name,
      role: decoded?.role || 'viewer',
    })
  }, [])

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY)
    setUser(null)
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        register,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}

export function getToken(): string | null {
  return getRawToken()
}
